import { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Textarea,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    useDisclosure,
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
} from "@heroui/react";
import { LuArrowLeft, LuPlus, LuTrash2, LuSave } from "react-icons/lu";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { usePostData, useFetchData } from "@/hooks";
import { showToast, formatCurrency, findOptionTitle } from "@/utils";
import { ThemedText, PageHeader } from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";
import { BASE_API } from "@/constants";

const InitialStockCreate = () => {
    const navigate = useNavigate();

    // Units per index: { 0: { data: [], isLoading: false }, 1: { data: [], isLoading: false } }
    const [unitsPerIndex, setUnitsPerIndex] = useState({});

    // Confirmation Modal
    const confirmModal = useDisclosure();
    const [pendingData, setPendingData] = useState(null);

    // React Hook Form
    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm({
        defaultValues: {
            items: [
                {
                    material_id: "",
                    warehouse_id: "",
                    material_unit_id: "",
                    date: null,
                    qty_in: null,
                    price: null,
                    description: "",
                },
            ],
        },
    });

    // useFieldArray for dynamic rows
    const { fields, append, remove } = useFieldArray({
        control,
        name: "items",
    });

    // Watch all items for material changes
    const watchItems = watch("items");

    // Fetch dropdown data
    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");

    // Mutation
    const storeMutation = usePostData("stock-initial/store");

    // Fetch units for specific material and store at index
    const fetchUnitsForIndex = async (index, materialId) => {
        if (!materialId) {
            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: [], isLoading: false },
            }));
            return;
        }

        // Set loading
        setUnitsPerIndex((prev) => ({
            ...prev,
            [index]: { data: prev[index]?.data || [], isLoading: true },
        }));

        try {
            const response = await axios.get(
                `${BASE_API}/select/material-unit`,
                {
                    params: { material_id: materialId },
                },
            );
            const units = response.data.data || [];

            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: units, isLoading: false },
            }));

            // Auto-select if only one unit available
            if (units.length === 1) {
                setValue(
                    `items.${index}.material_unit_id`,
                    String(units[0].value),
                );
            }
        } catch (error) {
            console.error("Failed to fetch units:", error);
            setUnitsPerIndex((prev) => ({
                ...prev,
                [index]: { data: [], isLoading: false },
            }));
        }
    };

    // Handle material change
    const handleMaterialChange = (index, key) => {
        setValue(`items.${index}.material_id`, key || "");
        setValue(`items.${index}.material_unit_id`, "");
        fetchUnitsForIndex(index, key);
    };

    // Handle unit change
    const handleUnitChange = (index, key) => {
        setValue(`items.${index}.material_unit_id`, key || "");
    };

    // Add new row
    const handleAddRow = () => {
        append({
            material_id: "",
            warehouse_id: "",
            material_unit_id: "",
            date: null,
            qty_in: null,
            price: null,
            description: "",
        });
    };

    // Remove row
    const handleRemoveRow = (index) => {
        if (fields.length === 1) {
            showToast("warning", "Peringatan", "Minimal terisi satu item.");
            return;
        }
        remove(index);
        // Clean up units for removed index
        setUnitsPerIndex((prev) => {
            const newState = { ...prev };
            delete newState[index];
            return newState;
        });
    };

    // Submit
    const handleInitialSubmit = (data) => {
        // Validate items
        for (let i = 0; i < data.items.length; i++) {
            const item = data.items[i];
            if (!item.material_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Produk pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.warehouse_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Gudang pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.material_unit_id) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Satuan pada baris ${i + 1} wajib dipilih.`,
                );
                return;
            }
            if (!item.date) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Tanggal pada baris ${i + 1} wajib diisi.`,
                );
                return;
            }
            if (!item.qty_in || Number(item.qty_in) <= 0) {
                showToast(
                    "error",
                    "Validasi Gagal",
                    `Qty pada baris ${i + 1} wajib diisi dan lebih dari 0.`,
                );
                return;
            }
        }

        setPendingData(data);
        confirmModal.onOpen();
    };

    const handleConfirmSubmit = () => {
        if (!pendingData) return;

        const payload = {
            items: pendingData.items.map((item) => ({
                material_id: item.material_id,
                warehouse_id: item.warehouse_id,
                material_unit_id: item.material_unit_id,
                date: item.date,
                qty_in: Number(item.qty_in),
                price: item.price ? Number(item.price) : null,
                description: item.description || null,
            })),
        };

        storeMutation.mutate(payload, {
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    "Stok awal berhasil ditambahkan.",
                );
                navigate("/stock-initial");
            },
            onError: (err) => {
                let message =
                    "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";

                if (err.response?.status === 422) {
                    message =
                        "Mohon periksa kembali input Anda. Pastikan semua kolom terisi dengan benar.";
                }

                showToast("error", "Gagal", message);
            },
        });
    };

    // Helper to get labels
    const getMaterialLabel = (id) => findOptionTitle(materials, id);
    const getWarehouseLabel = (id) => findOptionTitle(warehouses, id);
    const getUnitLabel = (index, id) =>
        findOptionTitle(unitsPerIndex[index]?.data, id);

    const calculateTotal = () => {
        if (!pendingData?.items) return 0;
        return pendingData.items.reduce((acc, item) => {
            const price = Number(item.price || 0);
            const qty = Number(item.qty_in || 0);
            return acc + price * qty;
        }, 0);
    };

    return (
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
            {/* Header */}
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title="Tambah Stok Awal"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok Awal", href: "/stock-initial" },
                        { label: "Tambah" },
                    ]}
                />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit(handleInitialSubmit)}>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            Daftar Produk Stok Awal
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="flex flex-col gap-6 p-6">
                        {fields.map((field, index) => (
                            <Card
                                key={field.id}
                                className="border-default-200 bg-default-50/50 border"
                                shadow="none"
                            >
                                <CardBody className="p-5">
                                    {/* Item Header */}
                                    <div className="mb-5 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="bg-primary/10 text-primary flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold shadow-sm">
                                                {index + 1}
                                            </div>
                                            <div>
                                                <ThemedText className="text-default-900 font-semibold">
                                                    Item Stok
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    className="text-default-400"
                                                >
                                                    Isi detail stok barang
                                                </ThemedText>
                                            </div>
                                        </div>
                                        <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                            color="danger"
                                            className="opacity-70 hover:opacity-100"
                                            onPress={() =>
                                                handleRemoveRow(index)
                                            }
                                        >
                                            <LuTrash2 size={18} />
                                        </Button>
                                    </div>

                                    <div className="flex flex-col gap-4">
                                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
                                            {/* Left Column: Product Info */}
                                            <div className="flex flex-col gap-4 lg:col-span-5">
                                                <Autocomplete
                                                    label="Produk"
                                                    placeholder="Cari & pilih produk..."
                                                    isRequired
                                                    isLoading={
                                                        isLoadingMaterials
                                                    }
                                                    selectedKey={
                                                        watchItems[index]
                                                            ?.material_id
                                                            ? String(
                                                                  watchItems[
                                                                      index
                                                                  ].material_id,
                                                              )
                                                            : null
                                                    }
                                                    onSelectionChange={(key) =>
                                                        handleMaterialChange(
                                                            index,
                                                            key,
                                                        )
                                                    }
                                                    variant="bordered"
                                                    isInvalid={
                                                        !!errors.items?.[index]
                                                            ?.material_id
                                                    }
                                                    errorMessage={
                                                        errors.items?.[index]
                                                            ?.material_id
                                                            ?.message
                                                    }
                                                    className="max-w-full"
                                                >
                                                    {(materials || []).map(
                                                        (m) => (
                                                            <AutocompleteItem
                                                                key={String(
                                                                    m.value,
                                                                )}
                                                                value={m.value}
                                                            >
                                                                {m.title}
                                                            </AutocompleteItem>
                                                        ),
                                                    )}
                                                </Autocomplete>

                                                <div className="grid grid-cols-2 gap-4">
                                                    <Autocomplete
                                                        label="Gudang"
                                                        placeholder="Pilih lokasi"
                                                        isRequired
                                                        isLoading={
                                                            isLoadingWarehouses
                                                        }
                                                        selectedKey={
                                                            watchItems[index]
                                                                ?.warehouse_id
                                                                ? String(
                                                                      watchItems[
                                                                          index
                                                                      ]
                                                                          .warehouse_id,
                                                                  )
                                                                : null
                                                        }
                                                        onSelectionChange={(
                                                            key,
                                                        ) =>
                                                            setValue(
                                                                `items.${index}.warehouse_id`,
                                                                key || "",
                                                            )
                                                        }
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.items?.[
                                                                index
                                                            ]?.warehouse_id
                                                        }
                                                        errorMessage={
                                                            errors.items?.[
                                                                index
                                                            ]?.warehouse_id
                                                                ?.message
                                                        }
                                                    >
                                                        {(warehouses || []).map(
                                                            (w) => (
                                                                <AutocompleteItem
                                                                    key={String(
                                                                        w.value,
                                                                    )}
                                                                    value={
                                                                        w.value
                                                                    }
                                                                >
                                                                    {w.title}
                                                                </AutocompleteItem>
                                                            ),
                                                        )}
                                                    </Autocomplete>

                                                    <DatePicker
                                                        label="Tanggal"
                                                        isRequired
                                                        showMonthAndYearPickers
                                                        value={
                                                            watchItems[index]
                                                                ?.date
                                                                ? parseDate(
                                                                      watchItems[
                                                                          index
                                                                      ].date,
                                                                  )
                                                                : null
                                                        }
                                                        onChange={(date) =>
                                                            setValue(
                                                                `items.${index}.date`,
                                                                date
                                                                    ? date.toString()
                                                                    : "",
                                                            )
                                                        }
                                                        maxValue={today(
                                                            getLocalTimeZone(),
                                                        )}
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.items?.[
                                                                index
                                                            ]?.date
                                                        }
                                                        errorMessage={
                                                            errors.items?.[
                                                                index
                                                            ]?.date?.message
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            {/* Right Column: Qty & Price */}
                                            <div className="flex flex-col gap-4 lg:col-span-7">
                                                <div className="grid grid-cols-3 gap-4">
                                                    <div className="col-span-2">
                                                        <NumberInput
                                                            label="Qty"
                                                            placeholder="Contoh: 100"
                                                            isRequired
                                                            value={
                                                                watchItems[
                                                                    index
                                                                ]?.qty_in ??
                                                                undefined
                                                            }
                                                            onValueChange={(
                                                                val,
                                                            ) =>
                                                                setValue(
                                                                    `items.${index}.qty_in`,
                                                                    val,
                                                                )
                                                            }
                                                            variant="bordered"
                                                            minValue={0}
                                                            hideStepper
                                                            isInvalid={
                                                                !!errors
                                                                    .items?.[
                                                                    index
                                                                ]?.qty_in
                                                            }
                                                            errorMessage={
                                                                errors.items?.[
                                                                    index
                                                                ]?.qty_in
                                                                    ?.message
                                                            }
                                                        />
                                                    </div>
                                                    <Autocomplete
                                                        label="Satuan"
                                                        placeholder={
                                                            watchItems[index]
                                                                ?.material_id
                                                                ? "Pilih satuan"
                                                                : "Pilih produk dulu"
                                                        }
                                                        isRequired
                                                        isDisabled={
                                                            !watchItems[index]
                                                                ?.material_id
                                                        }
                                                        isLoading={
                                                            unitsPerIndex[index]
                                                                ?.isLoading
                                                        }
                                                        selectedKey={
                                                            watchItems[index]
                                                                ?.material_unit_id
                                                                ? String(
                                                                      watchItems[
                                                                          index
                                                                      ]
                                                                          .material_unit_id,
                                                                  )
                                                                : null
                                                        }
                                                        onSelectionChange={(
                                                            key,
                                                        ) =>
                                                            handleUnitChange(
                                                                index,
                                                                key,
                                                            )
                                                        }
                                                        variant="bordered"
                                                        isInvalid={
                                                            !!errors.items?.[
                                                                index
                                                            ]?.material_unit_id
                                                        }
                                                    >
                                                        {(
                                                            unitsPerIndex[index]
                                                                ?.data || []
                                                        ).map((u) => (
                                                            <AutocompleteItem
                                                                key={String(
                                                                    u.value,
                                                                )}
                                                                value={u.value}
                                                            >
                                                                {u.title}
                                                            </AutocompleteItem>
                                                        ))}
                                                    </Autocomplete>
                                                </div>

                                                <div className="w-full">
                                                    <NumberInput
                                                        label="Harga Beli (Satuan)"
                                                        placeholder="0"
                                                        value={
                                                            watchItems[index]
                                                                ?.price ??
                                                            undefined
                                                        }
                                                        onValueChange={(val) =>
                                                            setValue(
                                                                `items.${index}.price`,
                                                                val,
                                                            )
                                                        }
                                                        variant="bordered"
                                                        minValue={0}
                                                        hideStepper
                                                        startContent={
                                                            <ThemedText
                                                                size="sm"
                                                                variant="secondary"
                                                            >
                                                                Rp
                                                            </ThemedText>
                                                        }
                                                        formatOptions={{
                                                            style: "decimal",
                                                            minimumFractionDigits: 0,
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Keterangan Full Width Row */}
                                        <Textarea
                                            label="Keterangan"
                                            placeholder="Keterangan tambahan (opsional)"
                                            value={
                                                watchItems[index]
                                                    ?.description || ""
                                            }
                                            onValueChange={(val) =>
                                                setValue(
                                                    `items.${index}.description`,
                                                    val,
                                                )
                                            }
                                            variant="bordered"
                                            className="w-full"
                                        />
                                    </div>
                                </CardBody>
                            </Card>
                        ))}

                        {/* Add Row Button (Moved here) */}
                        <div>
                            <Button
                                type="button"
                                color="primary"
                                variant="flat"
                                size="sm"
                                startContent={<LuPlus size={16} />}
                                onPress={handleAddRow}
                            >
                                Tambah Item
                            </Button>
                        </div>
                    </CardBody>
                    <CardFooter className="flex justify-end gap-3 px-6 py-4">
                        <Button
                            as={Link}
                            href="/stock-initial"
                            type="button"
                            variant="flat"
                            size="sm"
                        >
                            Batal
                        </Button>
                        <Button
                            color="primary"
                            onPress={handleSubmit(handleInitialSubmit)}
                            isLoading={storeMutation.isPending}
                            startContent={<LuSave size={16} />}
                            size="sm"
                        >
                            Simpan Stok
                        </Button>
                    </CardFooter>
                </Card>

                {/* Confirmation Modal */}
                <Modal
                    isOpen={confirmModal.isOpen}
                    onOpenChange={confirmModal.onOpenChange}
                    size="3xl"
                    scrollBehavior="inside"
                    classNames={{
                        header: "border-b border-default-200",
                        footer: "border-t border-default-200",
                    }}
                >
                    <ModalContent>
                        {(onClose) => (
                            <>
                                <ModalHeader className="flex flex-col gap-1">
                                    <ThemedText>
                                        Konfirmasi Stok Awal
                                    </ThemedText>
                                    <ThemedText
                                        variant="secondary"
                                        size="xs"
                                        className="font-normal"
                                    >
                                        Mohon periksa kembali data di bawah ini
                                        sebelum menyimpan.
                                    </ThemedText>
                                </ModalHeader>
                                <ModalBody className="p-0">
                                    <Table
                                        aria-label="Summary Table"
                                        removeWrapper
                                    >
                                        <TableHeader>
                                            <TableColumn>No</TableColumn>
                                            <TableColumn>
                                                Produk & Lokasi
                                            </TableColumn>
                                            <TableColumn>Qty</TableColumn>
                                            <TableColumn>
                                                Harga Satuan
                                            </TableColumn>
                                            <TableColumn align="end">
                                                Subtotal
                                            </TableColumn>
                                        </TableHeader>
                                        <TableBody>
                                            {(pendingData?.items || []).map(
                                                (item, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            {idx + 1}.
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col">
                                                                <ThemedText
                                                                    className="font-semibold"
                                                                    size="sm"
                                                                >
                                                                    {getMaterialLabel(
                                                                        item.material_id,
                                                                    )}
                                                                </ThemedText>
                                                                <ThemedText
                                                                    variant="secondary"
                                                                    size="xs"
                                                                >
                                                                    {getWarehouseLabel(
                                                                        item.warehouse_id,
                                                                    )}
                                                                </ThemedText>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <ThemedText size="sm">
                                                                {item.qty_in}{" "}
                                                                {getUnitLabel(
                                                                    idx,
                                                                    item.material_unit_id,
                                                                )}
                                                            </ThemedText>
                                                        </TableCell>
                                                        <TableCell>
                                                            {item.price ? (
                                                                <ThemedText size="sm">
                                                                    {formatCurrency(
                                                                        item.price,
                                                                    )}
                                                                </ThemedText>
                                                            ) : (
                                                                <ThemedText
                                                                    variant="caption"
                                                                    size="xs"
                                                                >
                                                                    -
                                                                </ThemedText>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <ThemedText
                                                                variant="primary"
                                                                className="font-semibold"
                                                            >
                                                                {formatCurrency(
                                                                    (Number(
                                                                        item.price,
                                                                    ) || 0) *
                                                                        (Number(
                                                                            item.qty_in,
                                                                        ) || 0),
                                                                )}
                                                            </ThemedText>
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>

                                    <div className="bg-default-50 flex items-center justify-end gap-6 px-6 py-3">
                                        <ThemedText
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Total Estimasi Nilai Stok:
                                        </ThemedText>
                                        <ThemedText
                                            className="font-bold"
                                            variant="primary"
                                        >
                                            {formatCurrency(calculateTotal())}
                                        </ThemedText>
                                    </div>
                                </ModalBody>
                                <ModalFooter>
                                    <Button
                                        variant="light"
                                        onPress={onClose}
                                        size="sm"
                                        className="text-default-500 hover:text-default-900 font-medium"
                                    >
                                        Batal
                                    </Button>
                                    <Button
                                        color="primary"
                                        onPress={handleConfirmSubmit}
                                        isLoading={storeMutation.isPending}
                                        size="sm"
                                    >
                                        Konfirmasi Stok
                                    </Button>
                                </ModalFooter>
                            </>
                        )}
                    </ModalContent>
                </Modal>
            </form>
        </div>
    );
};

export default InitialStockCreate;
